// TODO: This file has become huge, I think is time to move some stuff like the features flags to a different store

import { get } from 'lodash'
import { defineStore } from 'pinia'
import * as Sentry from '@sentry/browser'
import { Service } from '@/services/helpers/ServiceHelper'
import { MS_PER } from '@/services/types/Time'
import type { AvailableLanguage, Maybe } from '@/types/generated'
import type { Refresher } from '@/services/types/Refresher'

const OPT_OUT_MESSAGE = 'Text STOP to unsubscribe'

type User = {
  currentTenant: string | null,
  userInfo: any,
  loadingUserInformation: boolean,
  loadingMenus: boolean,
  tenants: Array<any>,
  phoneMenus: Array<any> | null,
  token: string | null,
  availableLanguages: Array<any>,
  locale: string
}

type Message = {
  id: number,
  title: string,
  message: string,
  severity: string
}

const getDefaultState = () => ({
  currentTenant: localStorage.getItem('current-tenant-id'),
  userInfo: null,
  loadingUserInformation: false,
  loadingMenus: false,
  tenants: [],
  phoneMenus: null,
  token: null,
  availableLanguages: [],
  locale: 'en'
})

export const useUserStore = defineStore('user', () => {
  const router = useRouter()
  const route = useRoute()
  const { getEffectiveAuthToken } = useEffectiveAuthToken()
  const { goToApp } = useLogin()
  const { isMobileDevice, redirectToWebAppForLogout } = useDeviceStore()
  const dateTimeStore = useDateTimeStore()
  const threadStore = useThreadStore()
  const { showCampaignsMergedUI, canUseVoiceCalls } = useFeatureFlags()
  const { onlyLettersAndNumbers } = useFilters()
  const dialog = useDialog()
  const snackbar = useSnackbar()
  const chromeExtensionStore = useChromeExtensionStore()
  const { CAMPAINGS_PERMISSIONS } = useActivePhoneNumber()

  const user = ref<User>(getDefaultState())
  const userServicesPromise = ref<Promise<any> | null>(null)
  const userInformationPromise = ref<Promise<any> | null>(null)
  const messages = ref<Array<Message>>([])

  const currentTenantId: any = computed({
    get: () => user.value.currentTenant,
    set: (value) => {
      localStorage.setItem('current-tenant-id', <string>value)
      user.value.currentTenant = value
    }
  })

  const isAdminOrOwner = computed(() => {
    const tenantInformation = userInfo.value?.memberships.find((tenant: { tenant_id: any }) => tenant.tenant_id === currentTenantId.value)
    return ['admin', 'owner'].includes(tenantInformation?.type)
  })

  const userInfo = computed(() => user.value.userInfo)

  const loadingMenus = computed(() => user.value.loadingMenus)

  const loadingUserInformation = computed(() => user.value.loadingUserInformation)

  const tenants = computed(() => user.value.tenants)

  const availableLanguages = computed(() => user.value.availableLanguages)

  const locale = computed(() => user.value.locale)

  const phoneMenus = computed({
    get: () => {
      return user.value.phoneMenus
    },
    set: (value) => {
      const sortByInboxName = (inbox: any, otherInbox: any) => {
        if (!inbox.inbox_name && !otherInbox.inbox_name) {
          return onlyLettersAndNumbers(inbox.phone_number) < onlyLettersAndNumbers(otherInbox.phone_number) ? -1 : 1
        }
        if (!inbox.inbox_name) { return 1 }
        if (!otherInbox.inbox_name) { return -1 }
        return inbox.inbox_name.localeCompare(otherInbox.inbox_name)
      }
      user.value.phoneMenus = value?.map((phone: any) => {
        const menus = []
        if (phone.chat_enabled) {
          menus.push({
            to: `/inbox/${phone.tenantId}/${phone.id}`,
            title: 'Inbox',
            icon: 'mdi-inbox',
            hasNotification: false,
            description: 'Start a conversation by accessing the Inbox interface!'
          })
        }
        menus.push({
          to: `/contacts/${phone.tenantId}/${phone.id}`,
          title: 'Contacts',
          icon: 'mdi-contacts',
          description: 'Access, search and create new contacts.',
          hasNotification: false
        })
        const isWppPhoneNumber = !phone.enable_sms && phone.enable_whatsapp
        if (isWppPhoneNumber) {
          menus.push({
            to: `/whatsapp-templates/${phone.tenantId}/${phone.id}`,
            title: 'WhatsApp Templates',
            icon: 'mdi-whatsapp',
            description: 'Create and modify Whatsapp templates.',
            hasNotification: false
          })
        }
        const canViewCampaigns = phone.permissions?.includes(CAMPAINGS_PERMISSIONS.VIEW)
        if (showCampaignsMergedUI.value && canViewCampaigns && (phone.campaigns_enabled || phone.drip_campaigns_enabled)) {
          menus.push({
            to: isWppPhoneNumber ? `/oneoff-campaigns/${phone.tenantId}/${phone.id}` : `/drip-campaigns/${phone.tenantId}/${phone.id}`,
            title: 'Campaigns',
            description: 'Create and modify existing campaigns.',
            icon: 'mdi-bullhorn',
            hasNotification: false
          })
        } else {
          if (phone.campaigns_enabled && canViewCampaigns) {
            menus.push({
              to: `/oneoff-campaigns/${phone.tenantId}/${phone.id}`,
              title: 'Campaigns',
              description: 'Create and modify existing campaigns.',
              icon: 'mdi-bullhorn',
              hasNotification: false
            })
          }
          if (!isWppPhoneNumber && canViewCampaigns && (phone.campaigns_enabled || phone.drip_campaigns_enabled)) {
            menus.push({
              to: phone.drip_campaigns_enabled ? `/drip-campaigns/${phone.tenantId}/${phone.id}` : '/early-access/drip-campaigns/',
              title: 'Drip Campaigns',
              description: 'Create and modify existing drip campaigns.',
              icon: 'mdi-multicast',
              hasNotification: false
            })
          }
        }
        // Calls Logs Menu
        if (!phone.read_only && canUseVoiceCalls.value) {
          menus.push({
            to: `/call-logs/${phone.tenantId}/${phone.id}`,
            title: 'Call Logs',
            icon: 'mdi-phone-log',
            description: 'View the call history for this phone number.',
            hasNotification: false
          })
        }
        return { ...phone, menus }
      }).sort(sortByInboxName) || null
    }
  })

  const seatPhoneNumberId = computed(() => {
    return phoneMenus.value?.find(phone => phone.is_seat)?.id || null
  })

  const userServiceRefresh : Refresher = {
    interval: undefined,
    enable () {
      this.interval = setInterval(() => {
        loadUserServices(true)
      }, 10 * MS_PER.Minute)
    },
    disable () {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = undefined
      }
    }
  }

  const token = computed({
    get: () => user.value.token,
    set: (value) => {
      if (value) {
        localStorage.setItem('auth-token', <string>value)
        user.value.token = value
      } else {
        localStorage.removeItem('auth-token')
        user.value.token = null
      }
    }
  })

  const activePhoneMenuFromPath = (path:any) => {
    return phoneMenus.value?.find(phoneRoute => phoneRoute.menus.some((menu:any) => path === menu.to))
  }

  const phoneMenuById = (id: any) => {
    return phoneMenus.value?.find(phoneRoute => phoneRoute.id?.toString() === id?.toString())
  }

  const phoneById = (id:string) => {
    return user.value.tenants?.map(tenant => tenant.phone_numbers)
      .flat().find(phone => phone.id?.toString() === id?.toString())
  }

  const phoneByNumber = (number:any) => {
    return user.value.tenants?.map(tenant => tenant.phone_numbers)
      .flat().find(phone => phone.phone_number?.toString() === number?.toString())
  }

  const tenantOwnerOfPhone = (phone:any) => {
    return user.value.tenants?.find(tenant => tenant.phone_numbers.map(({ phone_number }:any) => phone_number).includes(phone))
  }

  const currentTenantInfo = computed(() => {
    return user.value.tenants?.find(tenant => tenant.id?.toString() === currentTenantId.value?.toString())
  })

  const userName = (phone:any) => {
    return user.value.tenants?.find(tenant => tenant.phone_numbers.map(({ phone_number }:any) => phone_number).includes(phone))
  }

  const checkIsReadOnly = (id:string) => {
    return phoneById(id)?.read_only
  }

  const getUserServicesPromise = async () => {
    if (phoneMenus.value) {
      // We want to load this asyncronously
      loadUserServices(false)
      return phoneMenus.value
    } else {
      await loadUserServices(false)
    }
  }

  const loadUserServices = async (skipLoading: boolean) => {
    try {
      userServiceRefresh.disable()
      user.value.loadingMenus = !skipLoading
      const services = await Service.api.userService?.getUserServices()
      user.value.tenants = services?.tenants || []
      if (services?.tenants) {
        Sentry.setContext('tenants', { tenants: JSON.stringify(services.tenants) })
      }
      let tenantId = currentTenantId.value || services?.tenants[0]?.id
      const currentTenantWholeInfo = user.value.tenants.find(tenant => tenant.id?.toString() === tenantId?.toString())
      if (!currentTenantWholeInfo) {
        tenantId = services?.tenants[0]?.id
      }
      if (tenantId) {
        setCurrentTenantInformation(tenantId)
      }
      await dateTimeStore.initTimezone()
      return phoneMenus.value
    } catch (error:any) {
      // Clear the memoized promise so a later call (e.g. after login) retries
      // with a valid token instead of reusing this failed result.
      userServicesPromise.value = null
      if (error.response?.status !== 401) {
        dialog.warning({
          text: 'There was an unexpected problem while loading the user services. Please click the button reload to load them again',
          title: 'Oops',
          action: () => { location.reload() },
          dialogProps: {
            confirmText: 'Reload',
            canClose: false,
            showCancel: false
          }
        })
      }
      if (error.message !== 'Network Error') {
        Sentry.captureException(error)
      }
      return false
    } finally {
      user.value.loadingMenus = false
      userServiceRefresh.enable()
    }
  }

  const getUserServices = () => {
    if (userServicesPromise.value) {
      return userServicesPromise.value
    } else {
      const getUserServicesPromiseBinded = getUserServicesPromise.bind(this)
      const userServicesPromiseToSet = getUserServicesPromiseBinded()
      userServicesPromise.value = userServicesPromiseToSet
      return userServicesPromiseToSet
    }
  }

  const setCurrentTenantInformation = (tenantId: string) => {
    const currentTenant = user.value.tenants.find(tenant => tenant.id?.toString() === tenantId?.toString())
    const phoneNumberWithTenantId = (phone:any, tenant:any) => ({ ...phone, tenantId: tenant.id })
    const tenantPhoneMenus = currentTenant.phone_numbers
      .map((phone:any) => phoneNumberWithTenantId(phone, currentTenant))
    tenantPhoneMenus.sort((phoneMenuA:any, phoneMenuB:any) => phoneMenuA.is_seat - phoneMenuB.is_seat)
    phoneMenus.value = tenantPhoneMenus
    currentTenantId.value = tenantId
  }

  const getUserInformationPromise = async () => {
    const authToken = await getEffectiveAuthToken()
    if (!authToken) {
      return false
    }
    user.value.loadingUserInformation = true
    try {
      const userService = Service.api.userService
      if (!userService) {
        snackbar.error('Could not load the user information.')
        return
      }
      const response = await userService.getUserInformation()
      user.value.userInfo = response.user
      if (response.user) {
        const { id, email, name } = response.user
        Sentry.setContext('user', { id, email, name })
        Sentry.setContext('feature_flags', { feature_flags: currentTenantInfo.value?.feature_flags })
        const mixpanel = useMixpanel()
        mixpanel.identify(id.toString(), email, name)
      }
      if (user.value?.tenants?.length) {
        setCurrentTenantInformation(currentTenantId.value)
      }
      messages.value = response.messages.length > 0 ? response.messages : []
      return response.user
    } catch {
      snackbar.error('Could not get the user information.')
      return false
    } finally {
      user.value.loadingUserInformation = false
    }
  }

  const getUserInformation = () => {
    if (userInformationPromise.value) {
      return userInformationPromise.value
    }
    const p = getUserInformationPromise()
    userInformationPromise.value = p
    p.finally(() => { userInformationPromise.value = null })
    return p
  }

  const authenticate = async ({ email, password } : any) => {
    const response = await Service.api.userService?.authenticate(email, password)
    token.value = response?.token || null
    if (token.value) {
      const mixpanel = useMixpanel()
      mixpanel.track('webgui.auth.login', {
        email,
        login_method: 'password',
        tenant_id: currentTenantId.value
      })
    }
    return response
  }

  const refreshLegacyToken = async () => {
    try {
      if (!token.value) { return false }
      const response = await Service.api.userService?.refreshToken(token.value)
      if (!response?.token) {
        throw new Error('No token returned from refresh')
      }
      token.value = response.token
      return true
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('There was an error refreshing the authentication token. Please login again.')
      router.push('/logout')
      return false
    }
  }

  const goToInboxOrHome = async () => {
    try {
      const editableExtension = chromeExtensionStore.editable.extension()
      const phoneMenus = await getUserServices()
      if (!phoneMenus) {
        router.push('/')
        return
      }
      const hasChromeExtension = editableExtension.value?.eid
      const firstMenuOfFirstPhone = get(phoneMenus, '0.menus.0.to')
      const canGoToInbox = phoneMenus?.length === 1 && firstMenuOfFirstPhone && !hasChromeExtension
      if (canGoToInbox) {
        router.push(firstMenuOfFirstPhone)
        return
      }
      if (hasChromeExtension) {
        router.push('/chrome-extension')
        return
      }
      router.push('/')
    } catch (error) {
      snackbar.error('There was an error navigating to the inbox or home.')
      Sentry.captureException(error)
    }
  }

  const logout = async () => {
    try {
      const mixpanel = useMixpanel()
      mixpanel.track('webgui.auth.logout', {
        user_id: userInfo.value?.id,
        tenant_id: currentTenantId.value
      })
      mixpanel.reset()
      try {
        await Service.api.userService?.logout()
      } catch (error) {
      // Ignore logout errors
      }
      const intercom = useIntercom()
      intercom.shutdown()
      Service.api.channelService?.resetPluginState()
      userServicesPromise.value = null
      userServiceRefresh.disable()
      user.value = getDefaultState()
      messages.value = []
      threadStore.resetState()
      token.value = null
      localStorage.removeItem('auth-token')
      localStorage.removeItem('current-tenant-id')
      localStorage.removeItem('current-version')
      localStorage.removeItem('stored-phone-number-id')
      if (await isMobileDevice()) {
        await Promise.allSettled([
          Service.capacitor.storageCapacitor?.clear(),
          Service.capacitor.cookiesCapacitor?.clearAllCookies()
        ])
        await redirectToWebAppForLogout()
        return
      }
      chromeExtensionStore.logout()
      const comesFromPlatform = route.query.comesFromPlatform
      if (comesFromPlatform) {
        goToApp()
        return
      }
      router.push({ path: '/login', query: route.query })
    } catch (error) {
      snackbar.error('There was an error during logout. Please try again.')
      Sentry.captureException(error)
    }
  }

  const getAvailableLanguages = async () => {
    if (user.value.availableLanguages.length > 0) {
      return
    }
    try {
      const availableLanguages: Maybe<AvailableLanguage>[] | null = await Service.gql.threadGql?.getAvailableLanguages() || []
      user.value.availableLanguages = availableLanguages?.map((language : Maybe<AvailableLanguage>) => ({
        title: language?.name,
        value: language?.code,
        isPopular: language?.isPopular
      })) || []
    } catch (error) {
      snackbar.error('There was an error loading Available Languages.')
      Sentry.captureException(error)
    }
  }

  const refreshUserCache = () => {
    location.reload()
  }

  return {
    readable: {
      userInfo,
      loadingUserInformation,
      loadingMenus,
      tenants,
      phoneMenus,
      availableLanguages,
      locale,
      messages,
      isAdminOrOwner,
      OPT_OUT_MESSAGE,
      seatPhoneNumberId
    },
    editable: {
      currentTenantId,
      token
    },
    activePhoneMenuFromPath,
    phoneMenuById,
    phoneById,
    phoneByNumber,
    tenantOwnerOfPhone,
    currentTenantInfo,
    userName,
    checkIsReadOnly,
    getUserServices,
    setCurrentTenantInformation,
    getUserInformation,
    authenticate,
    refreshLegacyToken,
    goToInboxOrHome,
    logout,
    getAvailableLanguages,
    refreshUserCache
  }
})
